import { useMemo } from 'react';
import { Database } from 'lucide-react';
import { SqlConsole, selectAllFrom, type SqlConsoleTransport } from '@speles7172/sql-console';
import '@speles7172/sql-console/styles.css';
import { backupPreviewQuery } from '@speles7172/backup-client/core';
import * as adminSqlApi from '../api/admin-sql';
import { useInTabbedPage } from '../components/common/TabbedPage';

const LATEST_SUFFIX = '_latest';

/**
 * What clicking a table in the sidebar puts in the editor.
 *
 * `SELECT *` is right for the live database and wrong for a backup. The history
 * tables are partitioned by `snapshot_ts`, so a bare select reads every
 * snapshot at once — and is billed for every one of them — while looking like
 * it returned one table. Worse on an aggregate: `count(*)` sums the table at
 * every point in its history and gives a plausible, meaningless number.
 *
 * `backupPreviewQuery` from `@speles7172/backup-client/core` answers that
 * properly. It needs the *history* database name, and the console reports the
 * selected one — which is the Glue name, so the pair is derived rather than
 * configured and nothing here has to know the stage.
 *
 * Built per database and cached, because the package validates the name when
 * the builder is made rather than on every click, and a throw inside a click
 * handler is not somewhere a validation error should surface. An unrecognised
 * name falls back to `SELECT *` instead.
 */
function createPreviewQuery(): (
  table: string,
  context: { engine?: string | undefined; database?: string | undefined },
) => string {
  const builders = new Map<
    string,
    ((table: string, context: { database?: string }) => string) | null
  >();

  return (table, context) => {
    const engine = context.engine as Parameters<typeof selectAllFrom>[1];
    const database = context.database;
    if (context.engine !== 'athena' || database === undefined) {
      return selectAllFrom(table, engine);
    }

    const history = database.endsWith(LATEST_SUFFIX)
      ? database.slice(0, -LATEST_SUFFIX.length)
      : database;

    if (!builders.has(history)) {
      try {
        builders.set(history, backupPreviewQuery({ database: history }));
      } catch {
        // Not a name the package will build Athena SQL from. Some other Athena
        // database, or a rename this code has not caught up with; either way a
        // plain select is a worse preview and not a broken screen.
        builders.set(history, null);
      }
    }

    const build = builders.get(history);
    return build === null || build === undefined
      ? selectAllFrom(table, engine)
      : build(table, { database });
  };
}

/**
 * The SQL Client.
 *
 * The whole screen is `<SqlConsole>` from `@speles7172/sql-console` — the
 * editor, the schema sidebar, the results grid, the tabs, the saved queries and
 * the export all come from the package. It replaces a page written here that
 * could only ever reach one hard-coded PostgreSQL connection.
 *
 * The same deliberate exception to the component rules that `AuditLogsPage` and
 * `BackupsPage` take, recorded in CLAUDE.md for the same reason: those rules
 * exist so screens built *here* stay consistent, and this screen is not built
 * here.
 *
 * What the change buys is the database picker. The endpoint registers the live
 * database and, where the backup stack is deployed, the two Athena views of the
 * backups — so "what did this record look like before Tuesday" is a query on
 * this screen rather than an export and a spreadsheet.
 */
export function AdminSqlClientPage() {
  const transport = useMemo<SqlConsoleTransport>(
    () => ({
      execute: (request) => adminSqlApi.execute(request),
      listDatabases: () => adminSqlApi.listDatabases(),
      listTables: (database) => adminSqlApi.listTables(database),
    }),
    // Built once: the console takes the transport as an identity and reloads
    // when it changes, so an object literal here is an endless refresh.
    [],
  );

  const previewQuery = useMemo(() => createPreviewQuery(), []);

  const embedded = useInTabbedPage();

  return (
    <div className="space-y-6">
      {!embedded && (
        <div className="flex items-center gap-3">
          <Database size={24} className="text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">SQL Client</h1>
        </div>
      )}

      {/* `title={null}` as the other two consoles are given it: the heading
          above is the page's, and a second one below would read as a mistake.

          `allowWriteMode` only shows the switch. Whether a write is permitted
          is the endpoint's decision — it refuses one against either backup
          outright, and the role that reaches Athena is granted no write at
          all. */}
      <SqlConsole transport={transport} title={null} allowWriteMode previewQuery={previewQuery} />
    </div>
  );
}
